import { StoreApi } from "zustand";
import { createStore } from "zustand/vanilla";
import { devtools } from "zustand/middleware";
import type { RoboflowUser } from "./types";
import { projectSelector as project } from "@rflocal/shared";
import * as Slices from "./slices";
import { WorkspaceState } from "./slices";
import { Offer } from "./types";

interface AccountState {
    initialized: boolean;
    user: Partial<RoboflowUser>;
    setUser: (u: Partial<RoboflowUser>, action?: string) => any;
}

interface GlobalState {
    activePromos: Offer[];
    isDarkMode: boolean;
    magicLabelSession: Record<string, any>;
    setMagicLabelSession: (session: Record<string, any>) => any;
    magicLabelActive: boolean;
    setMagicLabelActive: (active: boolean) => any;
    selectedImageForSuggestions: File | null;
    setSelectedImageForSuggestions: (image: File | null) => any;
    recentlyCreatedWorkspace: boolean;
    setRecentlyCreatedWorkspace: (created: boolean) => any;
    preferredProjectCreationTool: string | null;
    setPreferredProjectCreationTool: (tool: string) => any;
}

type CoreAppState = AccountState &
    WorkspaceState &
    GlobalState &
    Slices.DatasetState &
    Slices.LabelingSessionState &
    Slices.UniverseState &
    Slices.NasRunsState;

type WithSelectors<S> = S extends { getState: () => infer T }
    ? S & { use: { [K in keyof T]: () => T[K] } }
    : never;

function buildCoreAppState(set, get, api) {
    return {
        initialized: false,
        user: {},
        setUser: (u, action = "setUser") => {
            window.user = u;
            return set(() => ({ user: u }), false, action);
        }
    };
}

function buildGlobalState(set, get, api) {
    return {
        activePromos: [],
        setActivePromos: (promos: Offer[]) => set(() => ({ activePromos: promos })),
        isDarkMode:
            typeof window !== "undefined"
                ? localStorage.getItem("annotationLightMode") === null
                    ? false // Default to light mode (isDarkMode = false)
                    : localStorage.getItem("annotationLightMode") !== "true"
                : false,
        setIsDarkMode: (isDarkMode: boolean) => set(() => ({ isDarkMode })),
        magicLabelSession: {},
        setMagicLabelSession: (magicLabelSession: Record<string, any>) => {
            set(() => ({ magicLabelSession }));
        },
        magicLabelActive: false,
        setMagicLabelActive: (active: boolean) => set(() => ({ magicLabelActive: active })),
        selectedImageForSuggestions: null,
        setSelectedImageForSuggestions: (image: File) =>
            set(() => ({ selectedImageForSuggestions: image })),
        recentlyCreatedWorkspace:
            typeof window !== "undefined"
                ? localStorage.getItem("recentlyCreatedWorkspace") === "true"
                : false,
        setRecentlyCreatedWorkspace: (created: boolean) => {
            localStorage.setItem("recentlyCreatedWorkspace", created.toString());
            set(() => ({ recentlyCreatedWorkspace: created }));
        },
        preferredProjectCreationTool:
            typeof window !== "undefined"
                ? localStorage.getItem("preferredProjectCreationTool")
                : null,
        setPreferredProjectCreationTool: (tool: string) => {
            localStorage.setItem("preferredProjectCreationTool", tool);
            set(() => ({ preferredProjectCreationTool: tool }));
        }
    };
}

const createSelectors = <T extends StoreApi<object>>(_store: T) => {
    const store = _store as WithSelectors<typeof _store>;
    store.use = {};
    for (const k of Object.keys(store.getState())) {
        store.use[k] = store.getState()[k];
    }
    return store;
};

// Ensure consistent global reference across module reloads
declare global {
    var __store__: ReturnType<typeof createGlobalStore> | undefined;
    var currentDataset: {
        type?: string;
        id?: string;
        owner?: string;
        url?: string;
        annotation?: string;
        [key: string]: any;
    };
}

function createGlobalStore() {
    return createStore<CoreAppState>()(
        devtools(
            (...args) => ({
                ...buildCoreAppState(...args),
                ...Slices.buildWorkspaceStore(...args),
                ...Slices.buildDatasetStore(...args),
                ...buildGlobalState(...args),
                ...Slices.buildLabelingSessionStore(...args),
                ...Slices.buildUploadContextStore(...args),
                ...Slices.buildUniverseStore(...args),
                ...Slices.buildNasRunsStore(...args)
            }),
            {
                //trace: true, // Not very useful
                enabled: project.env() !== "prod"
            }
        )
    );
}

// Ensure consistent global reference across module reloads
function getGlobalStore() {
    if (!globalThis.__store__) {
        // @ts-expect-error Type mismatch between store instances
        globalThis.__store__ = createGlobalStore();
    }
    return globalThis.__store__;
}

export default createSelectors(getGlobalStore());
